import { useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { 
  Mail, Send, Building2, Globe, User, Truck, Copy, Check, 
  ChevronDown, ExternalLink, Loader2
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const USER_TYPES = [
  { 
    id: "firma", 
    label: "Firma", 
    icon: Building2, 
    color: "bg-blue-100 text-blue-700",
    registerPath: "/giris",
    subject: "Expo Haber'e Davetlisiniz - Fuar Sektörünün Dijital Platformu",
    body: `Merhaba,

Fuar ve stand tasarım sektörünün öncü dijital platformu Expo Haber'e hoş geldiniz!

Firmanızı Expo Haber'e kayıt ederek aşağıdaki avantajlardan yararlanabilirsiniz:

• Firma profilinizi binlerce sektör profesyoneline ulaştırın
• Referans projelerinizi ve portfolyonuzu sergileyin  
• Sektördeki güncel haberlere ve etkinliklere anında erişin
• Potansiyel müşteriler ve iş ortakları ile bağlantı kurun
• SEO dostu profil sayfanız ile Google'da üst sıralarda yer alın

Kayıt olmak tamamen ücretsizdir ve sadece birkaç dakikanızı alır.

Hemen kayıt olmak için: {{REGISTER_URL}}

Sorularınız için bize ulaşabilirsiniz.

Saygılarımızla,
Expo Haber Ekibi`
  },
  { 
    id: "organizator", 
    label: "Organizatör", 
    icon: Globe, 
    color: "bg-purple-100 text-purple-700",
    registerPath: "/organizator-giris",
    subject: "Expo Haber'e Davetlisiniz - Fuar Organizatörleri Platformu",
    body: `Merhaba,

Türkiye'nin önde gelen fuar ve etkinlik sektörü platformu Expo Haber, organizatör firmanızı aramızda görmek istiyor!

Organizatör olarak kayıt olduğunuzda:

• Fuarlarınızı ve etkinliklerinizi geniş kitlelere duyurun
• Organizatör profilinizi sektör profesyonellerine tanıtın
• Yaklaşan etkinliklerinizi takvimimizde ücretsiz listeleyin
• Stand tasarım firmaları ve tedarikçilerle iletişim kurun
• Sektör haberlerini ve trendleri takip edin

Kayıt olmak tamamen ücretsizdir.

Hemen kayıt olmak için: {{REGISTER_URL}}

Sorularınız için bize ulaşabilirsiniz.

Saygılarımızla,
Expo Haber Ekibi`
  },
  { 
    id: "freelancer", 
    label: "Freelancer", 
    icon: User, 
    color: "bg-amber-100 text-amber-700",
    registerPath: "/freelancer-giris",
    subject: "Expo Haber'e Davetlisiniz - Freelancer Portfolyo Platformu",
    body: `Merhaba,

Fuar sektöründe çalışan freelancer'lar için özel olarak tasarlanmış Expo Haber platformuna davetlisiniz!

Freelancer olarak kayıt olduğunuzda:

• Portfolyonuzu ve çalışmalarınızı sergileyin
• 3D tasarımlarınızı, render'larınızı ve projelerinizi paylaşın
• Stand tasarım firmaları ve organizatörlerle tanışın
• Sektördeki iş fırsatlarından haberdar olun
• Profesyonel profilinizi oluşturun ve müşteri kazanın

Kayıt olmak tamamen ücretsizdir.

Hemen kayıt olmak için: {{REGISTER_URL}}

Sorularınız için bize ulaşabilirsiniz.

Saygılarımızla,
Expo Haber Ekibi`
  },
  { 
    id: "tedarikci", 
    label: "Tedarikçi", 
    icon: Truck, 
    color: "bg-teal-100 text-teal-700",
    registerPath: "/tedarikci-giris",
    subject: "Expo Haber'e Davetlisiniz - Tedarikçi Firmaları Platformu",
    body: `Merhaba,

Fuar sektörünün ihtiyaç duyduğu hizmetleri sunan tedarikçi firmalar için Expo Haber platformu sizleri bekliyor!

Tedarikçi olarak kayıt olduğunuzda:

• Catering, lojistik, hostes, teknik ekipman gibi hizmetlerinizi tanıtın
• Fuar firmaları ve organizatörlerle doğrudan iletişim kurun
• Hizmet alanlarınızı ve referanslarınızı sergileyin
• Sektördeki ihaleleri ve iş fırsatlarını takip edin
• Profesyonel firma profilinizi oluşturun

Kayıt olmak tamamen ücretsizdir.

Hemen kayıt olmak için: {{REGISTER_URL}}

Sorularınız için bize ulaşabilirsiniz.

Saygılarımızla,
Expo Haber Ekibi`
  }
];

export const InvitationModule = () => {
  const [selectedType, setSelectedType] = useState(USER_TYPES[0]);
  const [email, setEmail] = useState("");
  const [customSubject, setCustomSubject] = useState("");
  const [customBody, setCustomBody] = useState("");
  const [copied, setCopied] = useState(false);
  const [sending, setSending] = useState(false);

  const baseUrl = window.location.origin;

  const getRegisterUrl = () => {
    return `${baseUrl}${selectedType.registerPath}`;
  };

  const getSubject = () => {
    return customSubject || selectedType.subject;
  };

  const getBody = () => {
    const body = customBody || selectedType.body;
    return body.replace("{{REGISTER_URL}}", getRegisterUrl());
  };

  const handleSendEmail = async () => {
    if (!email || !email.includes("@")) {
      toast.error("Geçerli bir e-posta adresi girin");
      return;
    }

    setSending(true);
    try {
      await axios.post(`${API}/admin/send-invitation`, {
        recipient_email: email,
        user_type: selectedType.id,
        subject: getSubject(),
        body: getBody()
      });
      toast.success(`Davet e-postası ${email} adresine gönderildi!`);
      setEmail("");
    } catch (error) {
      console.error("Email send error:", error);
      toast.error(error.response?.data?.detail || "E-posta gönderilemedi");
    } finally {
      setSending(false);
    }
  };

  const handleCopyEmail = async () => {
    const fullText = `Konu: ${getSubject()}\n\n${getBody()}`;
    try {
      await navigator.clipboard.writeText(fullText);
      setCopied(true);
      toast.success("E-posta içeriği kopyalandı");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Kopyalama başarısız");
    }
  };

  const handleTypeChange = (type) => {
    setSelectedType(type);
    setCustomSubject("");
    setCustomBody("");
  };

  return (
    <div className="bg-white rounded-lg border border-stone-200 p-6 space-y-6" data-testid="invitation-module">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-[#1B4F72] to-[#2E86C1] rounded-lg flex items-center justify-center">
            <Mail className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-stone-900">Davet Gönder</h3>
            <p className="text-xs text-stone-400">Yeni kullanıcıları platforma davet edin</p>
          </div>
        </div>
      </div>

      {/* User Type Selection */}
      <div>
        <Label className="text-xs text-stone-500 mb-2 block">Kullanıcı Tipi Seçin</Label>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {USER_TYPES.map((type) => {
            const Icon = type.icon;
            const isSelected = selectedType.id === type.id;
            return (
              <button
                key={type.id}
                onClick={() => handleTypeChange(type)}
                className={`flex items-center gap-2 p-3 rounded-lg border-2 transition-all ${
                  isSelected 
                    ? "border-[#1B4F72] bg-blue-50" 
                    : "border-stone-200 hover:border-stone-300 bg-white"
                }`}
                data-testid={`type-${type.id}`}
              >
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${type.color}`}>
                  <Icon className="w-4 h-4" />
                </div>
                <span className={`text-sm font-medium ${isSelected ? "text-[#1B4F72]" : "text-stone-700"}`}>
                  {type.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Email Input */}
      <div>
        <Label className="text-xs text-stone-500 mb-1.5 block">Alıcı E-posta Adresi</Label>
        <Input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="ornek@firma.com"
          className="rounded-md"
          data-testid="invitation-email"
        />
      </div>

      {/* Email Preview */}
      <div className="bg-stone-50 rounded-lg p-4 space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium text-stone-500 uppercase tracking-wider">E-posta Önizleme</span>
          <Badge className={selectedType.color}>{selectedType.label}</Badge>
        </div>
        
        <div>
          <Label className="text-[10px] text-stone-400 uppercase tracking-wider">Konu</Label>
          <Input
            value={customSubject || selectedType.subject}
            onChange={(e) => setCustomSubject(e.target.value)}
            className="mt-1 bg-white rounded-md text-sm"
            placeholder="E-posta konusu..."
            data-testid="invitation-subject"
          />
        </div>

        <div>
          <Label className="text-[10px] text-stone-400 uppercase tracking-wider">İçerik</Label>
          <Textarea
            value={customBody || selectedType.body.replace("{{REGISTER_URL}}", getRegisterUrl())}
            onChange={(e) => setCustomBody(e.target.value)}
            rows={10}
            className="mt-1 bg-white rounded-md text-sm font-mono"
            data-testid="invitation-body"
          />
        </div>

        <div className="flex items-center gap-2 pt-2">
          <span className="text-[10px] text-stone-400">Kayıt Linki:</span>
          <a 
            href={getRegisterUrl()} 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-xs text-[#1B4F72] hover:underline flex items-center gap-1"
          >
            {getRegisterUrl()} <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-3 pt-2">
        <Button
          onClick={handleSendEmail}
          className="flex-1 rounded-md bg-[#1B4F72] hover:bg-[#154360] gap-2"
          disabled={!email || sending}
          data-testid="send-invitation"
        >
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          {sending ? "Gönderiliyor..." : "E-posta Gönder"}
        </Button>
        <Button
          onClick={handleCopyEmail}
          variant="outline"
          className="rounded-md gap-2"
          data-testid="copy-invitation"
        >
          {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
          {copied ? "Kopyalandı" : "Kopyala"}
        </Button>
      </div>

      <p className="text-[10px] text-stone-400 text-center">
        E-posta SendGrid üzerinden doğrudan gönderilecektir.
      </p>
    </div>
  );
};

export default InvitationModule;
